import React, { useEffect, useState } from 'react';
import {
  FaSearch,
  FaTimes,
  FaToolbox,
  FaTruck,
  FaTools,
} from 'react-icons/fa';
import { AvailableAsset, Asset } from '../services/PlantafelService';
import { AssetAssignment } from './leftPanelTypes';
import { pdebugError } from '../utils/plantafelDebug';
import styles from './PlantafelLeftPanel.module.css';

interface LeftPanelAssetsProps {
  isActive: boolean;
  assignedAssetMap?: Map<number, AssetAssignment>;
  onHighlightShift?: (shiftId: number) => void;
}

const getAssetIcon = (assetType?: string) => {
  switch (assetType?.toUpperCase()) {
    case 'VEHICLE':
    case 'TRAILER':
      return <FaTruck size={12} />;
    default:
      return <FaTools size={12} />;
  }
};

const isBasicEquipmentName = (name: string) =>
  /werkzeugkoffer|bohrmaschine|akkuschrauber|schraubendreher|hammer|zange|säge|leiter|handschuhe|helm|schutzbrille|maßband|wasserwaage|cuttermesser|taschenlampe|kabeltrommel/i.test(name);

export function LeftPanelAssets({
  isActive,
  assignedAssetMap,
  onHighlightShift,
}: LeftPanelAssetsProps): React.JSX.Element {
  const [assets, setAssets] = useState<AvailableAsset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [clickedAssetId, setClickedAssetId] = useState<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const loadAssets = async () => {
      setLoadingAssets(true);
      try {
        const response = await import('../../../config/axios').then(m => m.default.get('/api/admin/assets/'));
        const allAssets = Array.isArray(response.data)
          ? response.data
          : response.data?.assets || response.data?.results || [];
        const activeAssets = allAssets.filter((a: Asset) => a.is_active !== false && a.status === 'ACTIVE');
        setAssets(activeAssets.map((a: Asset) => ({
          id: a.id,
          name: a.name,
          code: a.code,
          asset_type: a.asset_type,
          category: a.category,
          license_plate: a.license_plate,
          status: a.status,
          is_active: a.is_active,
          availability_status: 'available' as const,
        })));
      } catch (err) {
        pdebugError('Failed to load assets:', err);
        setAssets([]);
      } finally {
        setLoadingAssets(false);
      }
    };

    loadAssets();
  }, [isActive]);

  const searchLower = assetSearch.toLowerCase();
  const filteredAssets = assetSearch
    ? assets.filter(a =>
        a.name.toLowerCase().includes(searchLower) ||
        a.code?.toLowerCase().includes(searchLower) ||
        a.license_plate?.toLowerCase().includes(searchLower)
      )
    : assets;

  const vehicles = filteredAssets.filter(a =>
    a.asset_type?.toUpperCase() === 'VEHICLE' || a.asset_type?.toUpperCase() === 'TRAILER'
  );
  const basicEquipment = filteredAssets.filter(a =>
    a.asset_type?.toUpperCase() !== 'VEHICLE' &&
    a.asset_type?.toUpperCase() !== 'TRAILER' &&
    isBasicEquipmentName(a.name)
  );
  const specializedAssets = filteredAssets.filter(a =>
    a.asset_type?.toUpperCase() !== 'VEHICLE' &&
    a.asset_type?.toUpperCase() !== 'TRAILER' &&
    !isBasicEquipmentName(a.name)
  );

  const renderAssetPill = (asset: AvailableAsset) => {
    const assignment = assignedAssetMap?.get(asset.id);
    const isAssigned = !!assignment;
    const isClicked = clickedAssetId === asset.id;
    const canDrag = asset.availability_status !== 'busy' || isAssigned;
    const waColor = assignment?.workAreaColor || '#7c3aed';

    return (
      <div
        key={asset.id}
        draggable={canDrag}
        onClick={() => {
          if (isAssigned && onHighlightShift) {
            setClickedAssetId(asset.id);
            onHighlightShift(assignment.shiftId);
            setTimeout(() => setClickedAssetId(null), 1500);
          }
        }}
        onDragStart={(e) => {
          if (!canDrag) {
            e.preventDefault();
            return;
          }
          e.dataTransfer.setData('assetId', asset.id.toString());
          e.dataTransfer.setData('assetName', asset.name);
          if (isAssigned) {
            e.dataTransfer.setData('fromShiftId', assignment.shiftId.toString());
          }
          e.dataTransfer.effectAllowed = 'move';
        }}
        className={styles.assetPill}
        style={{
          background: isAssigned ? `${waColor}15` : undefined,
          border: `1px solid ${isAssigned ? waColor : '#e0e3ef'}`,
          borderLeft: isAssigned ? `3px solid ${waColor}` : undefined,
          cursor: canDrag ? 'grab' : 'not-allowed',
          opacity: canDrag ? 1 : 0.5,
          boxShadow: isClicked ? `0 0 0 2px ${waColor}, 0 0 12px ${waColor}80` : undefined,
          transform: isClicked ? 'scale(1.03)' : undefined,
        }}
        title={
          isAssigned
            ? `${asset.name} → ${assignment.jobTitle} (Klick zum Anzeigen, Drag zum Umplanen)`
            : `${asset.name}${asset.license_plate ? ' • ' + asset.license_plate : ''}`
        }
      >
        <span style={{ color: isAssigned ? waColor : '#666', flexShrink: 0 }}>
          {getAssetIcon(asset.asset_type)}
        </span>
        <div className={styles.assetPillBody}>
          <div className={styles.assetPillName}>
            {asset.license_plate || asset.name}
          </div>
          {isAssigned && (
            <div
              className={styles.assetPillAssignment}
              style={{ color: waColor }}
            >
              &rarr; {assignment.jobTitle}
            </div>
          )}
          {!isAssigned && asset.license_plate && (
            <div className={styles.assetPillSubName}>
              {asset.name}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.searchBarContainer}>
        <div className={styles.searchBarInner}>
          <FaSearch size={10} color="#888" />
          <input
            type="text"
            placeholder="Asset suchen..."
            value={assetSearch}
            onChange={(e) => setAssetSearch(e.target.value)}
            className={styles.searchInput}
          />
          {assetSearch && (
            <button
              onClick={() => setAssetSearch('')}
              className={styles.searchClearButton}
            >
              <FaTimes size={8} />
            </button>
          )}
        </div>
        <div className={styles.searchCount}>
          {filteredAssets.length} von {assets.length} Assets
        </div>
      </div>

      <div className={styles.assetsList}>
        {loadingAssets ? (
          <div className={styles.loadingStateSmall}>
            Lade Assets...
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className={styles.loadingStateSmall}>
            {assetSearch ? 'Keine Assets gefunden' : 'Keine verfügbaren Assets'}
          </div>
        ) : (
          <>
            {vehicles.length > 0 && (
              <div className={styles.assetSection}>
                <div className={`${styles.assetSectionHeader} ${styles.assetSectionHeaderGreen}`}>
                  <FaTruck size={10} />
                  Fahrzeuge ({vehicles.length})
                </div>
                <div className={styles.assetGrid}>
                  {vehicles.map(renderAssetPill)}
                </div>
              </div>
            )}

            {specializedAssets.length > 0 && (
              <div className={styles.assetSection}>
                <div className={`${styles.assetSectionHeader} ${styles.assetSectionHeaderGreen}`}>
                  <FaTools size={10} />
                  Spezialger&auml;te ({specializedAssets.length})
                </div>
                <div className={styles.assetGrid}>
                  {specializedAssets.map(renderAssetPill)}
                </div>
              </div>
            )}

            {basicEquipment.length > 0 && (
              <div>
                <div className={`${styles.assetSectionHeader} ${styles.assetSectionHeaderGray}`}>
                  <FaToolbox size={10} />
                  Grundausstattung ({basicEquipment.length})
                </div>
                <div className={styles.assetGrid}>
                  {basicEquipment.map(renderAssetPill)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
